import { useEffect, useMemo, useState, type ReactNode } from "react";
import type { AuthUser } from "../lib/auth";
import { GatewayControlsPanel } from "./GatewayControlsPanel";

const API_BASE = import.meta.env.VITE_AI_GATEWAY_URL || "";
const AUTH_BASE = import.meta.env.VITE_AUTH_URL || "https://auth.mattherbert.ca";
const LOCAL_ORG_STORAGE_KEY = "ai_app_org_drafts_v1";
const AI_PRODUCT = "ai";
const AI_CALLER = "ai";

type Tab = "overview" | "users" | "ai-rules" | "organizations" | "sessions" | "gateway";
type RuleScope = "product" | "tenant";

interface AdminPanelProps {
  user: AuthUser;
}

interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: string;
  product: string;
  created_at: string;
  last_login_at: string | null;
}

interface AuthSession {
  user_id: string;
  product: string;
  email: string;
  name: string | null;
  role: string;
  created_at: string;
  expires_at: string;
}

interface UsageBreakdown {
  group_key: string | null;
  provider: string;
  calls: number;
  total_cost_usd: number;
}

interface UsageData {
  period_days: number;
  total_cost_usd: number;
  total_calls: number;
  total_tokens: number;
  breakdown: UsageBreakdown[];
}

interface GatewayRule {
  id: string;
  rule_text: string;
  priority: number;
  scope: "global" | "product" | "tenant";
  product: string | null;
  tenant_id: string | null;
  enabled: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

interface BudgetCap {
  scope: "tenant" | "caller" | "user";
  scope_id: string;
  max_daily_cost_usd: number | null;
  max_monthly_cost_usd: number | null;
  max_response_cost_usd: number | null;
  enabled: boolean;
}

interface TuningConfig {
  scope: string;
  scope_id: string;
  system_prompt: string | null;
  temperature: number | null;
  route_mode: string | null;
  tools_enabled?: boolean;
  tools_allowlist?: string | null;
}

interface ToolDef {
  name: string;
  description?: string;
}

interface AdminChatSession {
  id: string;
  title: string | null;
  user_id: string;
  caller: string;
  tenant_id: string | null;
  total_cost_usd: number;
  message_count: number;
  created_at: string;
  updated_at: string;
}

interface SessionMessage {
  id: string;
  role: "system" | "user" | "assistant" | "tool" | string;
  content: string;
  created_at?: string;
}

interface LocalOrgMember {
  email: string;
  role: "owner" | "admin" | "member";
}

interface LocalOrgDraft {
  id: string;
  name: string;
  tier: string;
  ownerEmail: string;
  members: LocalOrgMember[];
  notes?: string;
  updatedAt: string;
}

interface OrganizationSummary {
  id: string;
  name: string;
  tier: string;
  ownerEmail: string;
  members: LocalOrgMember[];
  memberCount: number;
  liveUsageCostUsd: number;
  liveUsageCalls: number;
  budgetCap?: BudgetCap;
  tuning?: TuningConfig;
  rulesCount: number;
  hasLocalDraft: boolean;
}

const DEFAULT_AI_RULES = [
  "Always respond in the user's language (detect from their message)",
  "Never execute code, file operations, or API calls without explicit user approval",
  "When making factual claims, cite the source or indicate uncertainty",
  "For tool calls, explain what the tool will do before executing",
  "Respect organization-level rules set by the org admin",
];

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem("infra_token");
  const headers: Record<string, string> = { "content-type": "application/json" };
  if (token) headers.authorization = `Bearer ${token}`;
  return headers;
}

async function fetchJson<T>(path: string, opts?: RequestInit, base = API_BASE): Promise<T> {
  const res = await fetch(`${base}${path}`, {
    credentials: "include",
    ...opts,
    headers: {
      ...authHeaders(),
      ...((opts?.headers as Record<string, string> | undefined) || {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(body.error || `Request failed (${res.status})`);
  }
  return res.json();
}

function isAdminRole(role: string): boolean {
  return role === "admin" || role === "platform_admin" || role === "service";
}

function loadOrgDrafts(): LocalOrgDraft[] {
  try {
    const raw = localStorage.getItem(LOCAL_ORG_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function persistOrgDrafts(drafts: LocalOrgDraft[]) {
  localStorage.setItem(LOCAL_ORG_STORAGE_KEY, JSON.stringify(drafts));
}

function formatUsd(value?: number | null, precision = 2): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return `$${value.toFixed(precision)}`;
}

function formatDate(value?: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
}

function relativeTime(value?: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const diffMs = date.getTime() - Date.now();
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  const minutes = Math.round(diffMs / 60000);
  if (Math.abs(minutes) < 60) return rtf.format(minutes, "minute");
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 48) return rtf.format(hours, "hour");
  const days = Math.round(hours / 24);
  return rtf.format(days, "day");
}

function tabMeta(tab: Tab): { title: string; description: string } {
  switch (tab) {
    case "overview":
      return {
        title: "Admin overview",
        description: "AI App operational health, usage, and policy coverage at a glance.",
      };
    case "users":
      return {
        title: "Users",
        description: "AI App users, roles, active auth sessions, and 30-day usage.",
      };
    case "ai-rules":
      return {
        title: "AI rules",
        description: "Product and organization rules resolved by the shared gateway before model execution.",
      };
    case "organizations":
      return {
        title: "Organizations",
        description: "Lightweight org hierarchy built from live tenant-scoped gateway data plus local roster drafts.",
      };
    case "sessions":
      return {
        title: "Sessions",
        description: "Recent AI App chat sessions with search and transcript inspection.",
      };
    case "gateway":
      return {
        title: "Gateway controls",
        description: "Model, budget, tuning, and usage controls already wired to the shared AI gateway.",
      };
  }
}

export function AdminPanel({ user }: AdminPanelProps) {
  const [activeTab, setActiveTab] = useState<Tab>("overview");
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 3200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const showToast = (message: string, type: "success" | "error" = "success") =>
    setToast({ message, type });

  if (!isAdminRole(user.role)) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <div className="max-w-md rounded-2xl border border-border bg-card p-6 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-destructive/10 text-destructive">
            <ShieldIcon className="h-6 w-6" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">Admin access required</h2>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            Your account can use the AI app, but the admin surface is only available to users with an admin role.
          </p>
        </div>
      </div>
    );
  }

  const sections: Array<{ label: string; tabs: Tab[] }> = [
    { label: "Control center", tabs: ["overview"] },
    { label: "Administration", tabs: ["users", "ai-rules", "organizations", "sessions"] },
    { label: "Gateway", tabs: ["gateway"] },
  ];

  const meta = tabMeta(activeTab);

  return (
    <div className="h-full flex overflow-hidden bg-background">
      <aside className="hidden w-72 shrink-0 border-r border-border bg-card/60 lg:flex lg:flex-col">
        <div className="border-b border-border px-5 py-5">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <AdminIcon className="h-5 w-5" />
            </div>
            <div>
              <div className="text-sm font-semibold text-foreground">AI App Admin</div>
              <div className="text-xs text-muted-foreground">Rules, orgs, sessions, and controls</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4">
          {sections.map((section) => (
            <div key={section.label} className="mb-6">
              <div className="px-3 pb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                {section.label}
              </div>
              <div className="space-y-1">
                {section.tabs.map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`w-full rounded-xl px-3 py-2.5 text-left text-sm transition-colors ${
                      activeTab === tab
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:bg-background hover:text-foreground"
                    }`}
                  >
                    {tabMeta(tab).title}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="border-b border-border px-4 py-4 md:px-6">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                AI product administration
              </div>
              <h1 className="mt-1 text-2xl font-semibold tracking-tight text-foreground">{meta.title}</h1>
              <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{meta.description}</p>
            </div>
            <div className="rounded-xl border border-border bg-card px-3 py-2 text-xs text-muted-foreground">
              Signed in as <span className="font-medium text-foreground">{user.email}</span>
            </div>
          </div>
        </header>

        <div className="border-b border-border px-3 py-3 lg:hidden">
          <div className="flex gap-2 overflow-x-auto">
            {(["overview", "users", "ai-rules", "organizations", "sessions", "gateway"] as Tab[]).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`whitespace-nowrap rounded-lg px-3 py-2 text-sm transition-colors ${
                  activeTab === tab
                    ? "bg-primary text-primary-foreground"
                    : "bg-card text-muted-foreground hover:text-foreground"
                }`}
              >
                {tabMeta(tab).title}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {activeTab === "overview" && <OverviewTab />}
          {activeTab === "users" && <UsersTab showToast={showToast} />}
          {activeTab === "ai-rules" && <AiRulesTab showToast={showToast} />}
          {activeTab === "organizations" && <OrganizationsTab showToast={showToast} />}
          {activeTab === "sessions" && <SessionsTab />}
          {activeTab === "gateway" && (
            <div className="h-full">
              <GatewayControlsPanel />
            </div>
          )}
        </div>
      </div>

      {toast && (
        <div
          className={`fixed bottom-4 right-4 z-50 rounded-xl px-4 py-3 text-sm font-medium shadow-lg ${
            toast.type === "success"
              ? "bg-success text-success-foreground"
              : "bg-destructive text-destructive-foreground"
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}

function OverviewTab() {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [authSessions, setAuthSessions] = useState<AuthSession[]>([]);
  const [chatSessions, setChatSessions] = useState<AdminChatSession[]>([]);
  const [dailyUsage, setDailyUsage] = useState<UsageData | null>(null);
  const [orgUsage, setOrgUsage] = useState<UsageData | null>(null);
  const [tools, setTools] = useState<ToolDef[]>([]);
  const [effectiveRules, setEffectiveRules] = useState<GatewayRule[]>([]);
  const [sourceErrors, setSourceErrors] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      const [
        usersResult,
        authSessionsResult,
        chatSessionsResult,
        dailyUsageResult,
        orgUsageResult,
        toolsResult,
        rulesResult,
      ] = await Promise.allSettled([
        fetchJson<AdminUser[]>(`/admin/users?product=${AI_PRODUCT}`, undefined, AUTH_BASE),
        fetchJson<{ sessions: AuthSession[] }>(`/admin/sessions?product=${AI_PRODUCT}&limit=500`, undefined, AUTH_BASE),
        fetchJson<{ sessions: AdminChatSession[] }>(`/v1/admin/sessions?caller=${AI_CALLER}&limit=100`),
        fetchJson<UsageData>(`/v1/admin/cost?days=1&group_by=caller&caller=${AI_CALLER}`),
        fetchJson<UsageData>(`/v1/admin/cost?days=30&group_by=tenant&caller=${AI_CALLER}`),
        fetchJson<{ tools: ToolDef[] }>(`/v1/tools?caller=${AI_CALLER}`),
        fetchJson<{ rules: GatewayRule[] }>(`/v1/rules/effective?product=${AI_PRODUCT}`),
      ]);

      if (cancelled) return;

      const errors: string[] = [];

      if (usersResult.status === "fulfilled") setUsers(usersResult.value);
      else errors.push("users");

      if (authSessionsResult.status === "fulfilled") setAuthSessions(authSessionsResult.value.sessions || []);
      else errors.push("auth sessions");

      if (chatSessionsResult.status === "fulfilled") setChatSessions(chatSessionsResult.value.sessions || []);
      else errors.push("chat sessions");

      if (dailyUsageResult.status === "fulfilled") setDailyUsage(dailyUsageResult.value);
      else errors.push("daily usage");

      if (orgUsageResult.status === "fulfilled") setOrgUsage(orgUsageResult.value);
      else errors.push("organization usage");

      if (toolsResult.status === "fulfilled") setTools(toolsResult.value.tools || []);
      else errors.push("tool registry");

      if (rulesResult.status === "fulfilled") setEffectiveRules(rulesResult.value.rules || []);
      else errors.push("rules");

      setSourceErrors(errors);
      setLoading(false);
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const recentChatSessions = chatSessions.filter((session) => {
    const updated = new Date(session.updated_at).getTime();
    return !Number.isNaN(updated) && updated >= Date.now() - 24 * 60 * 60 * 1000;
  }).length;

  const orgCount = new Set(
    (orgUsage?.breakdown || [])
      .map((entry) => entry.group_key)
      .filter((value): value is string => Boolean(value && value !== "null")),
  ).size;

  if (loading) {
    return <LoadingState label="Loading overview" />;
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        <MetricCard label="AI users" value={users.length.toLocaleString()} helper="Auth-service users for product=ai" />
        <MetricCard label="Active auth sessions" value={authSessions.length.toLocaleString()} helper="Current non-revoked sessions" />
        <MetricCard label="Chat sessions (24h)" value={recentChatSessions.toLocaleString()} helper="Recent AI App conversations" />
        <MetricCard label="Spend today" value={formatUsd(dailyUsage?.total_cost_usd, 4)} helper={`${dailyUsage?.total_calls || 0} calls today`} />
        <MetricCard label="Effective rules" value={effectiveRules.length.toLocaleString()} helper="Global + AI product rules resolved by gateway" />
        <MetricCard label="Registered tools" value={tools.length.toLocaleString()} helper="Gateway tool definitions for caller=ai" />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1.3fr_0.9fr]">
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-foreground">30-day organization footprint</h2>
            <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
              {orgCount} orgs
            </span>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Derived from tenant-scoped AI App usage already recorded by the gateway.
          </p>

          <div className="mt-4 space-y-3">
            {(orgUsage?.breakdown || []).slice(0, 8).map((entry) => (
              <div key={`${entry.group_key}-${entry.provider}`} className="rounded-xl border border-border bg-background px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="font-medium text-foreground">{entry.group_key || "unscoped"}</div>
                    <div className="text-xs text-muted-foreground">{entry.calls} calls · {entry.provider}</div>
                  </div>
                  <div className="text-right font-mono text-sm text-foreground">
                    {formatUsd(entry.total_cost_usd, 4)}
                  </div>
                </div>
              </div>
            ))}
            {(!orgUsage?.breakdown || orgUsage.breakdown.length === 0) && (
              <EmptyState title="No tenant-scoped AI usage yet" body="Organization summaries will appear as soon as tenant-scoped traffic is recorded through the AI gateway." />
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5">
          <h2 className="text-lg font-semibold text-foreground">Data source status</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            The admin UI is backed by the shared auth service and AI gateway. Missing sources degrade the page gracefully.
          </p>

          <div className="mt-4 space-y-3">
            {[
              { label: "Users", ok: !sourceErrors.includes("users") },
              { label: "Auth sessions", ok: !sourceErrors.includes("auth sessions") },
              { label: "Chat sessions", ok: !sourceErrors.includes("chat sessions") },
              { label: "Usage", ok: !sourceErrors.includes("daily usage") && !sourceErrors.includes("organization usage") },
              { label: "Rules", ok: !sourceErrors.includes("rules") },
              { label: "Tools", ok: !sourceErrors.includes("tool registry") },
            ].map((source) => (
              <div key={source.label} className="flex items-center justify-between rounded-xl border border-border bg-background px-4 py-3">
                <span className="text-sm text-foreground">{source.label}</span>
                <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${source.ok ? "bg-success/15 text-success" : "bg-warning/15 text-warning"}`}>
                  {source.ok ? "available" : "degraded"}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function UsersTab({ showToast }: { showToast: (message: string, type?: "success" | "error") => void }) {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [sessionCounts, setSessionCounts] = useState<Record<string, number>>({});
  const [usageByUser, setUsageByUser] = useState<Record<string, UsageBreakdown>>({});
  const [search, setSearch] = useState("");
  const [updating, setUpdating] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      const [usersResult, sessionsResult, usageResult] = await Promise.allSettled([
        fetchJson<AdminUser[]>(`/admin/users?product=${AI_PRODUCT}`, undefined, AUTH_BASE),
        fetchJson<{ sessions: AuthSession[] }>(`/admin/sessions?product=${AI_PRODUCT}&limit=500`, undefined, AUTH_BASE),
        fetchJson<UsageData>(`/v1/admin/cost?days=30&group_by=user&caller=${AI_CALLER}`),
      ]);

      if (cancelled) return;

      if (usersResult.status === "fulfilled") setUsers(usersResult.value);
      if (sessionsResult.status === "fulfilled") {
        const counts = (sessionsResult.value.sessions || []).reduce<Record<string, number>>((acc, session) => {
          acc[session.user_id] = (acc[session.user_id] || 0) + 1;
          return acc;
        }, {});
        setSessionCounts(counts);
      }
      if (usageResult.status === "fulfilled") {
        const map = (usageResult.value.breakdown || []).reduce<Record<string, UsageBreakdown>>((acc, entry) => {
          if (!entry.group_key) return acc;
          const current = acc[entry.group_key] || { ...entry, calls: 0, total_cost_usd: 0 };
          current.calls += entry.calls;
          current.total_cost_usd += entry.total_cost_usd;
          acc[entry.group_key] = current;
          return acc;
        }, {});
        setUsageByUser(map);
      }
      setLoading(false);
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const draftOrganizations = loadOrgDrafts();
  const organizationsByEmail = draftOrganizations.reduce<Record<string, string[]>>((acc, org) => {
    const emails = new Set<string>([org.ownerEmail, ...org.members.map((member) => member.email)].filter(Boolean));
    for (const email of emails) {
      acc[email] = [...(acc[email] || []), org.name || org.id];
    }
    return acc;
  }, {});

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return users;
    return users.filter((item) =>
      [item.name, item.email, item.role].some((value) => value?.toLowerCase().includes(query)),
    );
  }, [search, users]);

  async function updateRole(userId: string, role: string) {
    setUpdating(userId);
    try {
      const updated = await fetchJson<AdminUser>(
        `/admin/users/${userId}`,
        { method: "PATCH", body: JSON.stringify({ role }) },
        AUTH_BASE,
      );
      setUsers((prev) => prev.map((user) => (user.id === userId ? updated : user)));
      showToast("User role updated");
    } catch (error: any) {
      showToast(error.message || "Failed to update role", "error");
    } finally {
      setUpdating(null);
    }
  }

  if (loading) {
    return <LoadingState label="Loading users" />;
  }

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div className="rounded-2xl border border-border bg-card p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="text-sm font-medium text-foreground">AI App users</div>
            <div className="text-sm text-muted-foreground">Roles come from auth-service. Usage comes from the AI gateway 30-day breakdown.</div>
          </div>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search users"
            className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground md:max-w-sm"
          />
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-border bg-card">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="border-b border-border bg-background/60">
              <tr className="text-left text-xs uppercase tracking-[0.16em] text-muted-foreground">
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Organizations</th>
                <th className="px-4 py-3 text-right">Sessions</th>
                <th className="px-4 py-3 text-right">30d cost</th>
                <th className="px-4 py-3 text-right">30d calls</th>
                <th className="px-4 py-3">Last login</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((item) => {
                const usage = usageByUser[item.id];
                const organizations = organizationsByEmail[item.email] || [];
                return (
                  <tr key={item.id} className="border-b border-border last:border-0">
                    <td className="px-4 py-3">
                      <div className="font-medium text-foreground">{item.name || item.email}</div>
                      <div className="text-xs text-muted-foreground">{item.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={item.role}
                        onChange={(e) => void updateRole(item.id, e.target.value)}
                        disabled={updating === item.id}
                        className="rounded-lg border border-border bg-background px-2 py-1 text-sm text-foreground disabled:opacity-50"
                      >
                        <option value="user">user</option>
                        <option value="admin">admin</option>
                        <option value="platform_admin">platform_admin</option>
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      {organizations.length ? (
                        <div className="flex flex-wrap gap-2">
                          {organizations.map((org) => (
                            <span key={org} className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
                              {org}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-sm text-foreground">
                      {(sessionCounts[item.id] || 0).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-sm text-foreground">
                      {formatUsd(usage?.total_cost_usd, 4)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-sm text-foreground">
                      {(usage?.calls || 0).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{relativeTime(item.last_login_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {filteredUsers.length === 0 && <EmptyState title="No matching users" body="Try a different search term." />}
    </div>
  );
}

function AiRulesTab({ showToast }: { showToast: (message: string, type?: "success" | "error") => void }) {
  const [loading, setLoading] = useState(true);
  const [globalRules, setGlobalRules] = useState<GatewayRule[]>([]);
  const [rules, setRules] = useState<GatewayRule[]>([]);
  const [scope, setScope] = useState<RuleScope>("product");
  const [tenantId, setTenantId] = useState("");
  const [newText, setNewText] = useState("");
  const [newPriority, setNewPriority] = useState("100");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState("");
  const [saving, setSaving] = useState(false);

  async function loadRules(activeScope = scope, activeTenantId = tenantId) {
    setLoading(true);
    try {
      const [globalResponse, scopedResponse] = await Promise.all([
        fetchJson<{ rules: GatewayRule[] }>("/v1/rules?scope=global&enabled=true"),
        activeScope === "tenant"
          ? fetchJson<{ rules: GatewayRule[] }>(
              `/v1/rules?scope=tenant&tenant_id=${encodeURIComponent(activeTenantId)}${activeTenantId ? "" : "&enabled=true"}`,
            )
          : fetchJson<{ rules: GatewayRule[] }>(`/v1/rules?scope=product&product=${AI_PRODUCT}`),
      ]);
      setGlobalRules(globalResponse.rules || []);
      setRules(scopedResponse.rules || []);
    } catch (error: any) {
      showToast(error.message || "Failed to load rules", "error");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadRules();
  }, []);

  async function createRule() {
    if (!newText.trim()) {
      showToast("Rule text is required", "error");
      return;
    }
    if (scope === "tenant" && !tenantId.trim()) {
      showToast("Organization ID is required for tenant rules", "error");
      return;
    }
    setSaving(true);
    try {
      await fetchJson<{ rule: GatewayRule }>("/v1/rules", {
        method: "POST",
        body: JSON.stringify({
          rule_text: newText.trim(),
          priority: Number(newPriority || "100"),
          scope,
          product: scope === "product" ? AI_PRODUCT : null,
          tenant_id: scope === "tenant" ? tenantId.trim() : null,
        }),
      });
      setNewText("");
      setNewPriority("100");
      await loadRules(scope, tenantId);
      showToast("Rule created");
    } catch (error: any) {
      showToast(error.message || "Failed to create rule", "error");
    } finally {
      setSaving(false);
    }
  }

  async function saveEdit(ruleId: string) {
    if (!editingText.trim()) {
      showToast("Rule text is required", "error");
      return;
    }
    setSaving(true);
    try {
      await fetchJson(`/v1/rules/${ruleId}`, {
        method: "PUT",
        body: JSON.stringify({ rule_text: editingText.trim() }),
      });
      setEditingId(null);
      setEditingText("");
      await loadRules(scope, tenantId);
      showToast("Rule updated");
    } catch (error: any) {
      showToast(error.message || "Failed to update rule", "error");
    } finally {
      setSaving(false);
    }
  }

  async function toggleRule(rule: GatewayRule) {
    try {
      await fetchJson(`/v1/rules/${rule.id}`, {
        method: "PUT",
        body: JSON.stringify({ enabled: !rule.enabled }),
      });
      await loadRules(scope, tenantId);
    } catch (error: any) {
      showToast(error.message || "Failed to update rule", "error");
    }
  }

  async function deleteRule(ruleId: string) {
    if (!window.confirm("Delete this rule?")) return;
    try {
      await fetchJson(`/v1/rules/${ruleId}`, { method: "DELETE" });
      await loadRules(scope, tenantId);
      showToast("Rule deleted");
    } catch (error: any) {
      showToast(error.message || "Failed to delete rule", "error");
    }
  }

  async function seedDefaults() {
    const existing = new Set(
      rules
        .filter((rule) => rule.scope === "product" && rule.product === AI_PRODUCT)
        .map((rule) => rule.rule_text.trim().toLowerCase()),
    );
    const missing = DEFAULT_AI_RULES.filter((text) => !existing.has(text.toLowerCase()));
    if (missing.length === 0) {
      showToast("Default AI rules are already present");
      return;
    }
    setSaving(true);
    try {
      for (const [index, text] of missing.entries()) {
        await fetchJson("/v1/rules", {
          method: "POST",
          body: JSON.stringify({
            rule_text: text,
            priority: 100 + index * 10,
            scope: "product",
            product: AI_PRODUCT,
          }),
        });
      }
      await loadRules("product", "");
      setScope("product");
      setTenantId("");
      showToast("Default AI rules seeded");
    } catch (error: any) {
      showToast(error.message || "Failed to seed default rules", "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1.1fr_0.9fr]">
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-foreground">Resolved base rules</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Global gateway safeguards always apply before AI App-specific rules.
              </p>
            </div>
            <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
              {globalRules.length} global
            </span>
          </div>
          <div className="mt-4 space-y-3">
            {globalRules.map((rule) => (
              <RuleCard key={rule.id} rule={rule} readOnly />
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-foreground">Manage AI App rules</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Product rules apply to all AI App traffic. Tenant rules apply to a specific organization ID.
              </p>
            </div>
            <button
              onClick={() => void seedDefaults()}
              disabled={saving}
              className="rounded-xl bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
            >
              Seed defaults
            </button>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-[0.5fr_0.5fr_1fr]">
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value as RuleScope)}
              className="rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground"
            >
              <option value="product">Product rules</option>
              <option value="tenant">Organization rules</option>
            </select>
            <input
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              placeholder="org / tenant id"
              disabled={scope !== "tenant"}
              className="rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground disabled:opacity-50"
            />
            <button
              onClick={() => void loadRules(scope, tenantId)}
              className="rounded-xl border border-border bg-background px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-card"
            >
              Refresh rules
            </button>
          </div>

          <div className="mt-4 space-y-3 rounded-2xl border border-border bg-background p-4">
            <textarea
              value={newText}
              onChange={(e) => setNewText(e.target.value)}
              rows={3}
              placeholder="Add a new AI rule"
              className="w-full rounded-xl border border-border bg-card px-3 py-2 text-sm text-foreground"
            />
            <div className="flex flex-col gap-3 md:flex-row">
              <input
                type="number"
                value={newPriority}
                onChange={(e) => setNewPriority(e.target.value)}
                className="rounded-xl border border-border bg-card px-3 py-2 text-sm font-mono text-foreground md:w-40"
                placeholder="priority"
              />
              <button
                onClick={() => void createRule()}
                disabled={saving}
                className="rounded-xl bg-success px-4 py-2 text-sm font-medium text-success-foreground transition-colors hover:bg-success/90 disabled:opacity-50"
              >
                {saving ? "Saving…" : "Add rule"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <LoadingState label="Loading rules" />
      ) : rules.length === 0 ? (
        <EmptyState
          title={scope === "tenant" ? "No organization rules yet" : "No product rules yet"}
          body={scope === "tenant"
            ? "Enter an organization ID and add tenant-scoped rules for it."
            : "Use the default rule seed or create product-specific AI guidance manually."}
        />
      ) : (
        <div className="space-y-3">
          {rules.map((rule) => (
            <div key={rule.id} className="rounded-2xl border border-border bg-card p-4">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="flex-1">
                  {editingId === rule.id ? (
                    <div className="space-y-3">
                      <textarea
                        value={editingText}
                        onChange={(e) => setEditingText(e.target.value)}
                        rows={3}
                        className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => void saveEdit(rule.id)}
                          className="rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => {
                            setEditingId(null);
                            setEditingText("");
                          }}
                          className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="text-sm leading-relaxed text-foreground">{rule.rule_text}</div>
                      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        <Pill>{rule.scope}</Pill>
                        <Pill>priority {rule.priority}</Pill>
                        {rule.product && <Pill>{rule.product}</Pill>}
                        {rule.tenant_id && <Pill>{rule.tenant_id}</Pill>}
                        <span>{formatDate(rule.updated_at)}</span>
                      </div>
                    </>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => void toggleRule(rule)}
                    className={`rounded-lg px-3 py-2 text-sm font-medium ${
                      rule.enabled ? "bg-success/15 text-success" : "bg-warning/15 text-warning"
                    }`}
                  >
                    {rule.enabled ? "Enabled" : "Disabled"}
                  </button>
                  <button
                    onClick={() => {
                      setEditingId(rule.id);
                      setEditingText(rule.rule_text);
                    }}
                    className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => void deleteRule(rule.id)}
                    className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OrganizationsTab({ showToast }: { showToast: (message: string, type?: "success" | "error") => void }) {
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState<LocalOrgDraft[]>([]);
  const [usageByOrg, setUsageByOrg] = useState<Record<string, UsageBreakdown>>({});
  const [budgetByOrg, setBudgetByOrg] = useState<Record<string, BudgetCap>>({});
  const [tuningByOrg, setTuningByOrg] = useState<Record<string, TuningConfig>>({});
  const [rulesByOrg, setRulesByOrg] = useState<Record<string, number>>({});
  const [newOrg, setNewOrg] = useState({ id: "", name: "", tier: "business", ownerEmail: "" });
  const [pendingMemberEmail, setPendingMemberEmail] = useState<Record<string, string>>({});
  const [pendingMemberRole, setPendingMemberRole] = useState<Record<string, LocalOrgMember["role"]>>({});

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      const localDrafts = loadOrgDrafts();
      const [usageResult, budgetResult, tuningResult, rulesResult] = await Promise.allSettled([
        fetchJson<UsageData>(`/v1/admin/cost?days=30&group_by=tenant&caller=${AI_CALLER}`),
        fetchJson<{ caps: BudgetCap[] }>("/v1/admin/budget?scope=tenant"),
        fetchJson<{ configs: TuningConfig[] }>("/v1/admin/tuning?scope=tenant"),
        fetchJson<{ rules: GatewayRule[] }>("/v1/rules?scope=tenant&enabled=true"),
      ]);

      if (cancelled) return;

      setDrafts(localDrafts);

      if (usageResult.status === "fulfilled") {
        const usageMap = (usageResult.value.breakdown || []).reduce<Record<string, UsageBreakdown>>((acc, entry) => {
          if (!entry.group_key) return acc;
          const existing = acc[entry.group_key] || { ...entry, calls: 0, total_cost_usd: 0 };
          existing.calls += entry.calls;
          existing.total_cost_usd += entry.total_cost_usd;
          acc[entry.group_key] = existing;
          return acc;
        }, {});
        setUsageByOrg(usageMap);
      }

      if (budgetResult.status === "fulfilled") {
        setBudgetByOrg(
          (budgetResult.value.caps || []).reduce<Record<string, BudgetCap>>((acc, cap) => {
            acc[cap.scope_id] = cap;
            return acc;
          }, {}),
        );
      }

      if (tuningResult.status === "fulfilled") {
        setTuningByOrg(
          (tuningResult.value.configs || []).reduce<Record<string, TuningConfig>>((acc, config) => {
            acc[config.scope_id] = config;
            return acc;
          }, {}),
        );
      }

      if (rulesResult.status === "fulfilled") {
        const counts = (rulesResult.value.rules || []).reduce<Record<string, number>>((acc, rule) => {
          if (!rule.tenant_id) return acc;
          acc[rule.tenant_id] = (acc[rule.tenant_id] || 0) + 1;
          return acc;
        }, {});
        setRulesByOrg(counts);
      }

      setLoading(false);
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const organizations = useMemo<OrganizationSummary[]>(() => {
    const ids = new Set<string>([
      ...Object.keys(usageByOrg),
      ...Object.keys(budgetByOrg),
      ...Object.keys(tuningByOrg),
      ...Object.keys(rulesByOrg),
      ...drafts.map((draft) => draft.id),
    ]);

    return [...ids]
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b))
      .map((id) => {
        const draft = drafts.find((entry) => entry.id === id);
        const usage = usageByOrg[id];
        return {
          id,
          name: draft?.name || id,
          tier: draft?.tier || "unassigned",
          ownerEmail: draft?.ownerEmail || "",
          members: draft?.members || [],
          memberCount: draft?.members.length || 0,
          liveUsageCostUsd: usage?.total_cost_usd || 0,
          liveUsageCalls: usage?.calls || 0,
          budgetCap: budgetByOrg[id],
          tuning: tuningByOrg[id],
          rulesCount: rulesByOrg[id] || 0,
          hasLocalDraft: Boolean(draft),
        };
      });
  }, [budgetByOrg, drafts, rulesByOrg, tuningByOrg, usageByOrg]);

  function updateDrafts(next: LocalOrgDraft[]) {
    setDrafts(next);
    persistOrgDrafts(next);
  }

  function startDraftFromOrg(orgId: string) {
    const nextDrafts = [
      ...drafts,
      {
        id: orgId,
        name: orgId,
        tier: "business",
        ownerEmail: "",
        members: [],
        updatedAt: new Date().toISOString(),
      },
    ];
    updateDrafts(nextDrafts);
    showToast(`Started local org draft for ${orgId}`);
  }

  function updateDraft(orgId: string, updater: (draft: LocalOrgDraft) => LocalOrgDraft) {
    const nextDrafts = drafts.map((draft) =>
      draft.id === orgId ? { ...updater(draft), updatedAt: new Date().toISOString() } : draft,
    );
    updateDrafts(nextDrafts);
  }

  function createOrgDraft() {
    if (!newOrg.id.trim() || !newOrg.name.trim()) {
      showToast("Organization ID and name are required", "error");
      return;
    }
    if (drafts.some((draft) => draft.id === newOrg.id.trim())) {
      showToast("That organization ID already exists locally", "error");
      return;
    }
    updateDrafts([
      ...drafts,
      {
        id: newOrg.id.trim(),
        name: newOrg.name.trim(),
        tier: newOrg.tier,
        ownerEmail: newOrg.ownerEmail.trim(),
        members: [],
        updatedAt: new Date().toISOString(),
      },
    ]);
    setNewOrg({ id: "", name: "", tier: "business", ownerEmail: "" });
    showToast("Organization draft created");
  }

  function deleteDraft(orgId: string) {
    if (!window.confirm("Delete this local organization draft?")) return;
    updateDrafts(drafts.filter((draft) => draft.id !== orgId));
  }

  function addMember(orgId: string) {
    const email = (pendingMemberEmail[orgId] || "").trim().toLowerCase();
    if (!email) {
      showToast("Member email is required", "error");
      return;
    }
    const role = pendingMemberRole[orgId] || "member";
    updateDraft(orgId, (draft) => {
      if (draft.members.some((member) => member.email === email)) return draft;
      return {
        ...draft,
        members: [...draft.members, { email, role }],
      };
    });
    setPendingMemberEmail((prev) => ({ ...prev, [orgId]: "" }));
    setPendingMemberRole((prev) => ({ ...prev, [orgId]: "member" }));
  }

  function removeMember(orgId: string, email: string) {
    updateDraft(orgId, (draft) => ({
      ...draft,
      members: draft.members.filter((member) => member.email !== email),
    }));
  }

  function updateMemberRole(orgId: string, email: string, role: LocalOrgMember["role"]) {
    updateDraft(orgId, (draft) => ({
      ...draft,
      members: draft.members.map((member) => (member.email === email ? { ...member, role } : member)),
    }));
  }

  if (loading) {
    return <LoadingState label="Loading organizations" />;
  }

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1fr_0.85fr]">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Organization hierarchy</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Live tenant-scoped usage, budgets, tuning, and tenant rules come from the gateway. Team rosters and owner metadata are stored locally in the browser until the dedicated org API lands.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <input
              value={newOrg.id}
              onChange={(e) => setNewOrg((prev) => ({ ...prev, id: e.target.value }))}
              placeholder="org id"
              className="rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground"
            />
            <input
              value={newOrg.name}
              onChange={(e) => setNewOrg((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="display name"
              className="rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground"
            />
            <select
              value={newOrg.tier}
              onChange={(e) => setNewOrg((prev) => ({ ...prev, tier: e.target.value }))}
              className="rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground"
            >
              <option value="standard">standard</option>
              <option value="business">business</option>
              <option value="custom">custom</option>
              <option value="internal">internal</option>
            </select>
            <input
              value={newOrg.ownerEmail}
              onChange={(e) => setNewOrg((prev) => ({ ...prev, ownerEmail: e.target.value }))}
              placeholder="owner email"
              className="rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground"
            />
            <button
              onClick={createOrgDraft}
              className="rounded-xl bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 md:col-span-2"
            >
              Create local org draft
            </button>
          </div>
        </div>
      </div>

      {organizations.length === 0 ? (
        <EmptyState
          title="No organizations yet"
          body="Create a local org draft or wait for tenant-scoped AI traffic to appear through the gateway."
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {organizations.map((org) => {
            const draft = drafts.find((entry) => entry.id === org.id);
            return (
              <div key={org.id} className="rounded-2xl border border-border bg-card p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-lg font-semibold text-foreground">{org.name}</h3>
                      <Pill>{org.id}</Pill>
                      <Pill>{org.tier}</Pill>
                      {org.hasLocalDraft ? <Pill>draft roster</Pill> : <Pill>live only</Pill>}
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {org.ownerEmail ? `Owner: ${org.ownerEmail}` : "No owner email saved yet"}
                    </p>
                  </div>
                  {!org.hasLocalDraft ? (
                    <button
                      onClick={() => startDraftFromOrg(org.id)}
                      className="rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground"
                    >
                      Start roster draft
                    </button>
                  ) : (
                    <button
                      onClick={() => deleteDraft(org.id)}
                      className="rounded-xl border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                    >
                      Delete draft
                    </button>
                  )}
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3">
                  <MiniMetric label="30d cost" value={formatUsd(org.liveUsageCostUsd, 4)} />
                  <MiniMetric label="30d calls" value={org.liveUsageCalls.toLocaleString()} />
                  <MiniMetric label="Budget" value={formatUsd(org.budgetCap?.max_monthly_cost_usd)} />
                  <MiniMetric label="Tenant rules" value={org.rulesCount.toLocaleString()} />
                </div>

                <div className="mt-4 rounded-2xl border border-border bg-background p-4">
                  <div className="text-sm font-medium text-foreground">Shared gateway settings</div>
                  <div className="mt-2 grid grid-cols-1 gap-2 text-sm text-muted-foreground md:grid-cols-2">
                    <div>Route mode: <span className="text-foreground">{org.tuning?.route_mode || "—"}</span></div>
                    <div>Temperature: <span className="text-foreground">{org.tuning?.temperature ?? "—"}</span></div>
                    <div>Monthly cap: <span className="text-foreground">{formatUsd(org.budgetCap?.max_monthly_cost_usd)}</span></div>
                    <div>Daily cap: <span className="text-foreground">{formatUsd(org.budgetCap?.max_daily_cost_usd)}</span></div>
                  </div>
                </div>

                {draft && (
                  <div className="mt-4 rounded-2xl border border-border bg-background p-4">
                    <div className="mb-3 text-sm font-medium text-foreground">Local team roster draft</div>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <input
                        value={draft.name}
                        onChange={(e) => updateDraft(org.id, (current) => ({ ...current, name: e.target.value }))}
                        placeholder="display name"
                        className="rounded-xl border border-border bg-card px-3 py-2 text-sm text-foreground"
                      />
                      <select
                        value={draft.tier}
                        onChange={(e) => updateDraft(org.id, (current) => ({ ...current, tier: e.target.value }))}
                        className="rounded-xl border border-border bg-card px-3 py-2 text-sm text-foreground"
                      >
                        <option value="standard">standard</option>
                        <option value="business">business</option>
                        <option value="custom">custom</option>
                        <option value="internal">internal</option>
                      </select>
                      <input
                        value={draft.ownerEmail}
                        onChange={(e) => updateDraft(org.id, (current) => ({ ...current, ownerEmail: e.target.value }))}
                        placeholder="owner email"
                        className="rounded-xl border border-border bg-card px-3 py-2 text-sm text-foreground md:col-span-2"
                      />
                    </div>

                    <div className="mt-4 space-y-3">
                      {draft.members.map((member) => (
                        <div key={member.email} className="flex flex-col gap-2 rounded-xl border border-border bg-card px-3 py-3 md:flex-row md:items-center md:justify-between">
                          <div className="text-sm text-foreground">{member.email}</div>
                          <div className="flex items-center gap-2">
                            <select
                              value={member.role}
                              onChange={(e) => updateMemberRole(org.id, member.email, e.target.value as LocalOrgMember["role"])}
                              className="rounded-lg border border-border bg-background px-2 py-1 text-sm text-foreground"
                            >
                              <option value="owner">owner</option>
                              <option value="admin">admin</option>
                              <option value="member">member</option>
                            </select>
                            <button
                              onClick={() => removeMember(org.id, member.email)}
                              className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-1 text-sm text-destructive"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                      {draft.members.length === 0 && (
                        <div className="text-sm text-muted-foreground">No local members yet.</div>
                      )}
                    </div>

                    <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-[1fr_160px_140px]">
                      <input
                        value={pendingMemberEmail[org.id] || ""}
                        onChange={(e) => setPendingMemberEmail((prev) => ({ ...prev, [org.id]: e.target.value }))}
                        placeholder="member@company.com"
                        className="rounded-xl border border-border bg-card px-3 py-2 text-sm text-foreground"
                      />
                      <select
                        value={pendingMemberRole[org.id] || "member"}
                        onChange={(e) => setPendingMemberRole((prev) => ({ ...prev, [org.id]: e.target.value as LocalOrgMember["role"] }))}
                        className="rounded-xl border border-border bg-card px-3 py-2 text-sm text-foreground"
                      >
                        <option value="member">member</option>
                        <option value="admin">admin</option>
                        <option value="owner">owner</option>
                      </select>
                      <button
                        onClick={() => addMember(org.id)}
                        className="rounded-xl bg-success px-4 py-2 text-sm font-medium text-success-foreground transition-colors hover:bg-success/90"
                      >
                        Add member
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SessionsTab() {
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState<AdminChatSession[]>([]);
  const [selectedSession, setSelectedSession] = useState<AdminChatSession | null>(null);
  const [messagesBySession, setMessagesBySession] = useState<Record<string, SessionMessage[]>>({});
  const [search, setSearch] = useState("");
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const data = await fetchJson<{ sessions: AdminChatSession[] }>(`/v1/admin/sessions?caller=${AI_CALLER}&limit=100`);
        if (!cancelled) setSessions(data.sessions || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredSessions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return sessions;
    return sessions.filter((session) =>
      [session.id, session.title || "", session.user_id, session.tenant_id || "", session.caller]
        .join(" ")
        .toLowerCase()
        .includes(query),
    );
  }, [search, sessions]);

  async function inspectSession(session: AdminChatSession) {
    setSelectedSession(session);
    if (messagesBySession[session.id]) return;
    setDetailLoading(true);
    try {
      const data = await fetchJson<{ messages: SessionMessage[] }>(`/v1/sessions/${session.id}`);
      setMessagesBySession((prev) => ({ ...prev, [session.id]: data.messages || [] }));
    } finally {
      setDetailLoading(false);
    }
  }

  if (loading) {
    return <LoadingState label="Loading sessions" />;
  }

  return (
    <div className="grid h-full grid-cols-1 gap-4 p-4 md:p-6 xl:grid-cols-[0.95fr_1.05fr]">
      <div className="min-h-0 rounded-2xl border border-border bg-card p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Recent chat sessions</h2>
            <p className="mt-1 text-sm text-muted-foreground">Server-side list from the AI gateway, filtered to caller=ai.</p>
          </div>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search sessions"
            className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground md:max-w-sm"
          />
        </div>

        <div className="mt-4 space-y-3 overflow-y-auto">
          {filteredSessions.map((session) => (
            <button
              key={session.id}
              onClick={() => void inspectSession(session)}
              className={`w-full rounded-2xl border px-4 py-4 text-left transition-colors ${
                selectedSession?.id === session.id
                  ? "border-primary bg-primary/5"
                  : "border-border bg-background hover:bg-card"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate font-medium text-foreground">{session.title || "Untitled session"}</div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {session.id} · {session.user_id}
                  </div>
                </div>
                <div className="text-right text-xs text-muted-foreground">
                  <div>{session.message_count} msgs</div>
                  <div className="font-mono text-foreground">{formatUsd(session.total_cost_usd, 4)}</div>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <Pill>{session.caller}</Pill>
                {session.tenant_id && <Pill>{session.tenant_id}</Pill>}
                <span>{relativeTime(session.updated_at)}</span>
              </div>
            </button>
          ))}
          {filteredSessions.length === 0 && <EmptyState title="No sessions found" body="Try a different search term." compact />}
        </div>
      </div>

      <div className="min-h-0 rounded-2xl border border-border bg-card p-4">
        {!selectedSession ? (
          <EmptyState title="Select a session" body="Choose a recent session on the left to inspect its transcript." />
        ) : (
          <>
            <div className="border-b border-border pb-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-foreground">{selectedSession.title || "Untitled session"}</h2>
                  <div className="mt-1 text-sm text-muted-foreground">
                    {selectedSession.user_id}
                    {selectedSession.tenant_id ? ` · ${selectedSession.tenant_id}` : ""}
                  </div>
                </div>
                <div className="text-right text-xs text-muted-foreground">
                  <div>Created {formatDate(selectedSession.created_at)}</div>
                  <div>Updated {formatDate(selectedSession.updated_at)}</div>
                </div>
              </div>
            </div>

            <div className="mt-4 space-y-3 overflow-y-auto">
              {detailLoading ? (
                <LoadingState label="Loading transcript" compact />
              ) : (
                (messagesBySession[selectedSession.id] || []).map((message, index) => (
                  <div key={`${selectedSession.id}-${index}`} className="rounded-2xl border border-border bg-background p-4">
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium capitalize text-primary">
                        {message.role}
                      </span>
                      <span className="text-xs text-muted-foreground">{formatDate(message.created_at)}</span>
                    </div>
                    <div className="whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground">
                      {message.content}
                    </div>
                  </div>
                ))
              )}
              {!detailLoading && (messagesBySession[selectedSession.id] || []).length === 0 && (
                <EmptyState title="No messages recorded" body="This session currently has no stored transcript rows." compact />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function RuleCard({ rule, readOnly = false }: { rule: GatewayRule; readOnly?: boolean }) {
  return (
    <div className={`rounded-2xl border border-border p-4 ${readOnly ? "bg-background" : "bg-card"}`}>
      <div className="text-sm leading-relaxed text-foreground">{rule.rule_text}</div>
      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <Pill>{rule.scope}</Pill>
        <Pill>priority {rule.priority}</Pill>
        {rule.product && <Pill>{rule.product}</Pill>}
        {rule.tenant_id && <Pill>{rule.tenant_id}</Pill>}
      </div>
    </div>
  );
}

function MetricCard({ label, value, helper }: { label: string; value: string; helper: string }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className="mt-2 text-3xl font-semibold tracking-tight text-foreground">{value}</div>
      <div className="mt-2 text-sm text-muted-foreground">{helper}</div>
    </div>
  );
}

function MiniMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border bg-background px-3 py-3">
      <div className="text-xs uppercase tracking-[0.14em] text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm font-medium text-foreground">{value}</div>
    </div>
  );
}

function Pill({ children }: { children: ReactNode }) {
  return (
    <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
      {children}
    </span>
  );
}

function EmptyState({ title, body, compact = false }: { title: string; body: string; compact?: boolean }) {
  return (
    <div className={`rounded-2xl border border-dashed border-border bg-card text-center ${compact ? "p-5" : "p-8"}`}>
      <div className="text-base font-medium text-foreground">{title}</div>
      <div className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</div>
    </div>
  );
}

function LoadingState({ label, compact = false }: { label: string; compact?: boolean }) {
  return (
    <div className={`flex items-center justify-center ${compact ? "py-8" : "py-20"}`}>
      <div className="flex items-center gap-3 rounded-2xl border border-border bg-card px-4 py-3 text-sm text-muted-foreground">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-muted-foreground/25 border-t-primary" />
        {label}…
      </div>
    </div>
  );
}

function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function AdminIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2a3 3 0 0 0-3 3v1H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-3V5a3 3 0 0 0-3-3z" />
      <line x1="8" y1="12" x2="16" y2="12" />
      <line x1="8" y1="16" x2="16" y2="16" />
    </svg>
  );
}
